import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { addToCart } from "@/store/slices/cartSlice";
import { removeFromWishlist } from "@/store/slices/uiSlice";
import { getProductById } from "@/api/products";
import LoadingScreen from "@/components/LoadingScreen";
import { EmptyWishlist } from "@/components/EmptyState";

const Wishlist = () => {
  const dispatch = useDispatch();
  const wishlistIds = useSelector((s) => s.ui.wishlist);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchWishlistItems = async () => {
      try {
        setLoading(true);
        const productPromises = wishlistIds.map((id) => getProductById(id));
        const resolvedProducts = await Promise.all(productPromises);
        setItems(resolvedProducts.filter(Boolean));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (wishlistIds.length > 0) {
      fetchWishlistItems();
    } else {
      setItems([]);
      setLoading(false);
    }
  }, [wishlistIds]);

  if (loading) return <LoadingScreen message="Loading your wishlist..." />;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Failed to load wishlist
          </h2>
          <p className="text-slate-600 mb-6">
            We're having trouble loading your wishlist. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <div className="container max-w-7xl mx-auto py-10">
        {items.length !== 0 && (
          <div className="text-center mb-6">
            <h1 className="font-display text-3xl">Your Wishlist</h1>
            <p className="text-slate-600">
              Save items you love and move them to your cart anytime.
            </p>
          </div>
        )}

        {items.length === 0 ? (
          <EmptyWishlist />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p) => (
              <div key={p._id} className="bg-white rounded-2xl p-4 shadow">
                <Link to={`/product/${p._id}`} className="block">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
                    <img
                      src={p.images?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
                <div className="mt-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-slate-700 font-semibold">
                      ₹{p.price}
                    </div>
                  </div>
                  <IconButton
                    aria-label="remove"
                    onClick={() => dispatch(removeFromWishlist(p._id))}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </div>
                <div className="mt-3 flex gap-3">
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() =>
                      dispatch(
                        addToCart({
                          productId: p._id,
                          name: p.name,
                          price: p.price,
                          image: p.images?.[0],
                        })
                      )
                    }
                  >
                    Add to Cart
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to={`/product/${p._id}`}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
